/**
 * Shared (client- and server-safe) constants and pure helpers for the admin
 * MFA-path authorization gate (Access Control Policy v2.1).
 *
 * Kept separate from `admin-auth-methods.ts` (which is `server-only`) so the
 * browser `userStore` can mirror the rule for UI gating without importing
 * server-only code.
 */

/**
 * AMR (Authentication Methods Reference) `method` values that satisfy the admin
 * authorization gate — the MFA-protected paths (Google OAuth, SAML SSO).
 *
 * ALLOWLIST, fail-closed: any method not listed (e.g. `otp`, `magiclink`,
 * `password`, `email`) denies admin privileges. This MUST remain an allowlist —
 * the custom-OTP sign-in path (`verifyCustomOtp`) authenticates via `magiclink`,
 * NOT `otp`, so a blocklist of `otp` would be silently bypassable.
 *
 * NOTE: the exact strings (especially `sso/saml`) must be confirmed against real
 * tokens in a deployed environment (Phase 0 spike) before enforcement is enabled.
 */
export const ALLOWED_ADMIN_AMR_METHODS = ["oauth", "sso/saml"] as const;

/**
 * Where the "sign out and re-authenticate" CTA sends an admin who hit the MFA-path
 * gate (`wrong-auth-method`): sign out, then back to login with a notice banner.
 * Shared so the multiple admin-gate surfaces (route guard, admin pages) stay in sync.
 */
export const ADMIN_REAUTH_CTA_HREF = `/logout?redirect=${encodeURIComponent("/login?message=admin-reauth")}`;

/**
 * User-facing message shown when an admin email is turned away from the email-OTP
 * sign-in path (Access Control Policy v2.1). Admins must use an MFA-protected path
 * (Google OAuth or SAML SSO), so the OTP send/verify steps reject the email before
 * a code is ever issued. Shared so the login flow and any future surface stay in sync.
 */
export const ADMIN_MFA_LOGIN_REQUIRED_MESSAGE =
  "This account must sign in with Google or SSO. Please use one of those options above.";

/**
 * User-facing message shown when an email belonging to an org with `enforce_sso`
 * is turned away from the email-OTP / Google paths (per-org SSO enforcement).
 * Such members must sign in through the org's SAML IdP. Shared so the login flow
 * (`EmailSignIn`, the `?message=sso-required` notice banner) and any future
 * surface stay in sync.
 */
export const SSO_REQUIRED_MESSAGE = "Your organization requires single sign-on. Please continue with SSO.";

/**
 * Pure predicate: does an `amr` claim contain at least one of `methods`?
 *
 * Supabase emits `amr` in two shapes: the detailed `{ method, timestamp }[]` form and
 * the RFC-8176 bare-string form (`["oauth"]`). Both are accepted — a string entry is
 * treated as the method name itself.
 *
 * Defensive against unknown shapes (JWT claims are typed loosely): returns false for
 * non-arrays, empty arrays, and entries that resolve to no string method.
 *
 * @param amr - The raw `amr` claim from a verified JWT (`{ method, timestamp }[]` or `string[]`).
 * @param methods - The method names to match against.
 * @returns True iff at least one entry's method is in `methods`.
 */
export function amrHasAnyMethod(amr: unknown, methods: readonly string[]): boolean {
  if (!Array.isArray(amr) || amr.length === 0) return false;
  return amr.some((entry) => {
    const method = typeof entry === "string" ? entry : (entry as { method?: unknown } | null)?.method;
    return typeof method === "string" && methods.includes(method);
  });
}

/**
 * Pure predicate: does an `amr` claim contain at least one admin-allowed method?
 *
 * @param amr - The raw `amr` claim from a verified JWT.
 * @returns True iff at least one entry's method is in {@link ALLOWED_ADMIN_AMR_METHODS}.
 */
export function amrIsAllowed(amr: unknown): boolean {
  return amrHasAnyMethod(amr, ALLOWED_ADMIN_AMR_METHODS);
}
